import type { BeforeSendFn, PostHog } from 'posthog-js';

import { isDaylight, isSpark, Product } from './productHelpers';

import { logError } from '@/utils/logError';
import { envSwitch } from '@/utils/environmentUtils';

type GetDistinctIdFn = (opts: { variables: { email: string } }) => Promise<{
  data?: { posthogDistinctIdWithEmail?: { distinctId?: string | null } | null } | null;
}>;

export function linkPostHogIdentity(
  getDistinctId: GetDistinctIdFn,
  posthog: PostHog | undefined,
  email: string,
  logPrefix = '[PostHog]',
): Promise<void> {
  return getDistinctId({ variables: { email } })
    .then((result) => {
      const distinctId = result?.data?.posthogDistinctIdWithEmail?.distinctId;
      if (distinctId) { posthog?.identify(distinctId, { email }); }
    })
    .catch((e) => { logError(logPrefix, 'PostHog identify failed', e); });
}

export enum PosthogProgram {
  SLEEPIO = 'sleepio',
  SPARK = 'spark_direct',
  DAYLIGHT = 'daylight',
  DAYLIGHT_PLATGEN = 'daylight_platgen',
}

export const SLEEPIO_PROGRAM = PosthogProgram.SLEEPIO;

export const SPARK_PROGRAM = PosthogProgram.SPARK;

export const DAYLIGHT_PROGRAM = PosthogProgram.DAYLIGHT;

// Sleepio Canopy's own product identity for analytics — distinct from SLEEPIO_PROGRAM,
// which getProgram() also returns for canopy (it normalizes to the generic Sleepio bucket).
export const SLEEPIO_CANOPY_PROGRAM = 'sleepio-canopy';

export const postHogHost = 'https://us.i.posthog.com';

// All non-production US environments (dev, stage, pr, qa) use the same PostHog project
export const projectApiKey = envSwitch({
  local: '',
  dev: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  stage: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  pr: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  qa: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  prod: 'phc_8QW21BDgUVTJrtxo6cuV7vu8vMqp4020M9jIGoHCHWE', // Production
});

export const getProgram = (product: Product): PosthogProgram => {
  if (isSpark(product)) {
    return SPARK_PROGRAM;
  }
  if (isDaylight(product)) {
    return DAYLIGHT_PROGRAM;
  }
  return SLEEPIO_PROGRAM;
};

export const getPlatgenProgram = (product: Product): PosthogProgram => {
  if (isDaylight(product)) {
    return PosthogProgram.DAYLIGHT_PLATGEN;
  }
  return getProgram(product);
};

const SENSITIVE_URL_PARAMS = ['token', 'refresh_token', 'user_email'];
const URL_PROPERTIES_TO_SCRUB = ['$current_url', '$session_entry_url', '$referrer'];

// These leak via the app-handoff bounce (see CreatePassword.tsx) or the reset-password email link.
export const stripSensitiveUrlParams = (url: string): string => {
  try {
    const parsed = new URL(url);
    SENSITIVE_URL_PARAMS.forEach((param) => parsed.searchParams.delete(param));
    // The app-handoff bounce now carries these in the fragment instead of the query string.
    const hashParams = new URLSearchParams(parsed.hash.slice(1));
    SENSITIVE_URL_PARAMS.forEach((param) => hashParams.delete(param));
    parsed.hash = hashParams.toString();
    if (parsed.pathname.startsWith('/reset-password-form/')) {
      parsed.pathname = parsed.pathname.split('/').slice(0, -1).join('/');
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

export const beforeSend: BeforeSendFn = (captureResult) => {
  if (!captureResult) {
    return captureResult;
  }
  const properties = { ...captureResult.properties };
  URL_PROPERTIES_TO_SCRUB.forEach((key) => {
    if (typeof properties[key] === 'string') {
      properties[key] = stripSensitiveUrlParams(properties[key] as string);
    }
  });
  return { ...captureResult, properties };
};
